import { useCallback, useEffect, useState } from "react";
import { Input, type InputProps } from "@/components/base/input/input";
import { useFieldContext } from "..";
import { FieldErrors } from "./FieldErrors";

/**
 * Phone number field component for TanStack Form
 *
 * Features:
 * - Automatic US phone formatting: (555) 555-5555
 * - Input masking for better UX
 * - Validation state display
 * - Loading states during async validation
 * - Works with both form.Field and form.AppField
 *
 * @example
 * // With form.AppField (recommended)
 * <form.AppField name="phone">
 *   {(field) => (
 *     <field.PhoneField
 *       label="Phone Number"
 *       placeholder="(555) 555-5555"
 *       isRequired
 *     />
 *   )}
 * </form.AppField>
 *
 * @example
 * // With form.Field and validators
 * <form.Field
 *   name="phone"
 *   validators={{
 *     onChange: ({ value }) => {
 *       const digits = value.replace(/\D/g, '');
 *       if (digits.length > 0 && digits.length < 10) {
 *         return 'Phone must be 10 digits';
 *       }
 *       return undefined;
 *     },
 *     onBlur: ({ value }) => {
 *       const digits = value.replace(/\D/g, '');
 *       if (digits.length === 0) return undefined;
 *       if (digits.length !== 10) {
 *         return 'Phone must be exactly 10 digits';
 *       }
 *       return undefined;
 *     },
 *   }}
 * >
 *   {(field) => (
 *     <PhoneField
 *       field={field}
 *       label="Phone Number"
 *       placeholder="(555) 555-5555"
 *     />
 *   )}
 * </form.Field>
 */

interface PhoneFieldProps
	extends Omit<InputProps, "value" | "onChange" | "onBlur" | "name"> {
	/** Label text displayed above the input */
	label?: string;
	/** Field API instance (automatically provided with form.AppField) */
	field?: any;
	/** Placeholder text (defaults to formatted phone example) */
	placeholder?: string;
	/** Whether to show formatting hint below the input */
	showHint?: boolean;
}

/**
 * Format phone number as user types
 * Converts: "5551234567" -> "(555) 123-4567"
 */
const formatPhoneNumber = (value: string): string => {
	// Remove all non-digit characters
	const digits = value.replace(/\D/g, "");

	// Format based on digit count
	if (digits.length === 0) return "";
	if (digits.length <= 3) return `(${digits}`;
	if (digits.length <= 6) {
		return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
	}
	// Limit to 10 digits for US phone format
	const limited = digits.slice(0, 10);
	return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

/**
 * Extract raw digits from formatted phone number
 * Converts: "(555) 123-4567" -> "5551234567"
 */
const getRawPhoneNumber = (formatted: string): string => {
	return formatted.replace(/\D/g, "");
};

const PhoneField = ({
	label,
	placeholder = "",
	showHint = false,
	field: fieldProp,
	hint,
	...props
}: PhoneFieldProps) => {
	// Support both patterns:
	// 1. form.Field with explicit field prop
	// 2. form.AppField with useFieldContext
	const contextField = useFieldContext<string>();
	const field = fieldProp || contextField;

	// Local state for display value (formatted)
	const [displayValue, setDisplayValue] = useState<string>(() =>
		formatPhoneNumber(field.state.value || ""),
	);

	// Keep display value in sync when the underlying form value changes externally
	// (e.g. via form.reset from a parent effect). The useState initializer only runs
	// on mount, so if the form value changes after mount we must sync manually.
	const fieldValue = field.state.value;
	useEffect(() => {
		setDisplayValue(formatPhoneNumber(fieldValue || ""));
	}, [fieldValue]);

	/**
	 * Handle input changes with formatting
	 * Stores raw digits in form state, displays formatted version
	 */
	const handleChange = useCallback(
		(value: string) => {
			// Format for display
			const formatted = formatPhoneNumber(value);
			setDisplayValue(formatted);

			// Store raw digits in form state
			const rawDigits = getRawPhoneNumber(formatted);
			field.handleChange(rawDigits);
		},
		[field],
	);

	/**
	 * Handle blur event
	 * Updates display value and triggers validation
	 */
	const handleBlur = useCallback(() => {
		// Ensure display value is properly formatted
		const formatted = formatPhoneNumber(field.state.value || "");
		setDisplayValue(formatted);
		field.handleBlur();
	}, [field]);

	// Compute if field has error
	const hasError = field.state.meta.isTouched && !field.state.meta.isValid;

	// Compute hint message
	const hintMessage = (() => {
		if (hint) return hint;
		if (showHint && !hasError) {
			const digits = getRawPhoneNumber(displayValue);
			if (digits.length > 0 && digits.length < 10) {
				return `${10 - digits.length} more digit${10 - digits.length === 1 ? "" : "s"} needed`;
			}
		}
		return undefined;
	})();

	return (
		<div className="phone-field">
			<Input
				{...props}
				id={field.name}
				name={field.name}
				value={displayValue}
				onChange={handleChange}
				onBlur={handleBlur}
				label={label}
				placeholder={placeholder}
				hint={hintMessage}
				isInvalid={hasError}
				isDisabled={props.isDisabled || field.state.meta.isValidating}
				type="tel"
				inputMode="tel"
				autoComplete="tel"
				aria-label={label || "Phone number"}
				aria-invalid={hasError}
				aria-describedby={
					hasError ? `${field.name}-error` : `${field.name}-hint`
				}
			/>
			<FieldErrors meta={field.state.meta} />
		</div>
	);
};

PhoneField.displayName = "PhoneField";

export default PhoneField;
